import * as PIXI from "pixi.js";
import gsap from "gsap";
import { IGameSnacks } from "./lib/GameSnacks";

import App from "./service/App";
import AudioManager, { track_id } from "./service/AudioManager";
import ServiceLocator from "./ServiceLocator";
import GameState from "./state/GameState";
import PreloaderState from "./state/PreloaderState";
import SavedData from "./service/SavedData";
import State from "./lib/State";

import { Signal } from "signals";
import { IInit, Profile } from "./lib/game_snacks/IUser";
import MultiplayerEventManager from "./service/MultiplayerEventManager";
import Session from "./Session";


declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class Main
{
    // #region Attributes //

    // private:

    private _app: App = null;

    private _tickCount: number = null;
    private _elapsedTimeForFPS: number = null;
    private _lastTimeScale: number = null;
    private _lastInactivityTime: number = null;

    private _curState: State = null;

    private _isPrintFPS: boolean = null;

    private _onGlobalMuteUpdated: Signal = null;

    private readonly _kMaxElapsedTime: number = 1/30;

    // #endregion //


    public get onGlobalMuteUpdated(): Signal { return this._onGlobalMuteUpdated; }


    // #region Methods //

    public constructor() 
    {
        Howler.autoSuspend = false;

        this._tickCount = 0;
        this._elapsedTimeForFPS = 0;
        //const kUrlParams = new URLSearchParams( window.location.search );
        this._isPrintFPS = false;//!kUrlParams.has( "isPromo" );

        document.head.insertAdjacentHTML( "beforeend", "<style>"
            + "@font-face {"
                + "font-family: 'college_tm_regular';"
                + "src: url('resources/fonts/college-tm.regular.woff2') format('woff2'),"
                    + "url('resources/fonts/college-tm.regular.woff') format('woff');"
            + "}"
            + "@font-face {"
                + "font-family: 'jaro_regular';"
                + "src: url('resources/fonts/Jaro-Regular-VariableFont_opsz.woff2') format('woff2'),"
                    + "url('resources/fonts/Jaro-Regular-VariableFont_opsz.woff') format('woff');"
            + "}"
            + "@font-face {"
                + "font-family: 'teko_regular';"
                + "src: url('resources/fonts/Teko-Regular.woff2') format('woff2'),"
                    + "url('resources/fonts/Teko-Regular.woff') format('woff');"
            + "}"
            + "</style>" );

        PIXI.Loader.shared
            .add( "game_logo", "resources/img/preloader/title@2x.png" )
            .add( "loading_bg", "resources/img/preloader/loading_screen_background@2x.png" )
            .add( "loading_bar_base", "resources/img/preloader/loading_bar_base@2x.png" )
            .add( "loading_bar", "resources/img/preloader/loading_bar@2x.png" );
        PIXI.Loader.shared.onComplete.once( this._onPreloaderAssets_Loaded, this );
        PIXI.Loader.shared.load();
    }

        private _onPreloaderAssets_Loaded(): void
        {
            this._onGlobalMuteUpdated = new Signal();

            this._app = new App();
            this._app.init();

            ServiceLocator.main = this;
            ServiceLocator.app = this._app;
            ServiceLocator.audio = new AudioManager();

            if ( window.GameSnacks )
            {
                this._onGlobalMute_Updated( window.GameSnacks.audio.isEnabled() );
                window.GameSnacks.audio.subscribe( this._onGlobalMute_Updated.bind( this ) );
            }
            else
            {
                console.error( "Game Snacks object not found." );
                return;
            }

            ServiceLocator.resources = {
                pre_tower: PIXI.Loader.shared.resources.pre_tower,
                pre_bg: PIXI.Loader.shared.resources.pre_bg };
            ServiceLocator.saved = new SavedData();
            ServiceLocator.saved.load();

            ServiceLocator.audio.changeVolume( 0.3, track_id.MUSIC );
            ServiceLocator.audio.changeVolume( 1, track_id.SFX );
            ServiceLocator.audio.changeVolume( 0.6, track_id.AMBIENT );

            ServiceLocator.audio.mute( ServiceLocator.saved.data.isMuteMusic, track_id.MUSIC );
            ServiceLocator.audio.mute( ServiceLocator.saved.data.isMuteSfx, track_id.SFX );
            ServiceLocator.audio.mute( ServiceLocator.saved.data.isMuteSfx, track_id.AMBIENT );

            window.GameSnacks.game.onResume( () => {
                    ServiceLocator.audio.resume( track_id.MUSIC );
                    ServiceLocator.audio.resume( track_id.SFX );
                    ServiceLocator.audio.resume( track_id.AMBIENT );

                    gsap.globalTimeline.timeScale( this._lastTimeScale !== null ? this._lastTimeScale : 1 );

                    Session.i.inactivityElapsedTime += ( Date.now() - this._lastInactivityTime ) / 1000;
                } );

            window.GameSnacks.game.onPause( () => {
                    ServiceLocator.audio.pause( track_id.MUSIC );
                    ServiceLocator.audio.pause( track_id.SFX );
                    ServiceLocator.audio.pause( track_id.AMBIENT );

                    this._lastTimeScale = gsap.globalTimeline.timeScale();
                    gsap.globalTimeline.timeScale( 0 );

                    this._lastInactivityTime = Date.now();
                } );

            this._app.gameDiv.style.backgroundImage = "none";
            this._app.gameDiv.style.backgroundSize = "auto 100%";
            this._app.gameDiv.style.backgroundPosition = "center";
            this._app.gameDiv.style.backgroundRepeat = "none";
            
            this._curState = this._createPreloaderState();

            gsap.ticker.add( this._onTick.bind( this ) );
        }

            private _createPreloaderState(): PreloaderState
            {
                let preloaderState: PreloaderState = new PreloaderState();
                preloaderState.init();
                preloaderState.onEnter();
                preloaderState.onComplete.addOnce( this._onPreloader_Complete, this );
                return preloaderState;
            }

                private _onPreloader_Complete(): void 
                {
                    this._curState.onLeave();
                    this._curState.end();

                    let gameState: GameState = new GameState();
                    gameState.init();
                    gameState.onEnter();
                    this._curState = gameState;
                    //
                    ServiceLocator.game = gameState;
                }

            private _onGlobalMute_Updated( isAudioEnabled: boolean ): void
            {
                // TEST:
                Howler.mute( !isAudioEnabled );

                this._onGlobalMuteUpdated.dispatch( isAudioEnabled );
            }

            private _onTick( time: number, deltaTime: number ): void
            {
                let elapsedTimeInSeconds: number = deltaTime * 0.001;
                if ( elapsedTimeInSeconds > this._kMaxElapsedTime )
                {
                    elapsedTimeInSeconds = this._kMaxElapsedTime;
                }
                this._curState.onUpdate( elapsedTimeInSeconds );

                this._tickCount += 1;
                this._elapsedTimeForFPS += elapsedTimeInSeconds;
                if ( this._elapsedTimeForFPS >= 1 )
                {            
                    if ( this._isPrintFPS )
                    {    
                        this._app.printFPS( this._tickCount );
                    }

                    this._tickCount = 0;
                    this._elapsedTimeForFPS = 0;
                }

                this._app.render();
            }

    // #endregion //
}

// When the index page has finished loading, create our app.
window.onload = () =>
{
    var main: Main = new Main();
}